import { FC, ReactNode } from 'react';
import { cn } from '@nexelem/utils';
import { statusColors, StatusColors } from './statusColors';
import clsx from 'clsx';
import { cva } from 'class-variance-authority';

export type StatusProps = {
  className?: string;
  color?: StatusColors;
  variant?: 'contained' | 'outlined' | 'dot' | 'icon';
  Icon?: ReactNode | FC<{ className?: string }>;
  text?: string;
  showDot?: boolean;
  size?: 'md' | 'large' | 'xl';
};

type DotProps = {
  color: StatusColors;
  className?: string;
};

const Dot = ({ color, className }: DotProps) => (
  <div
    className={cn(
      'box-border h-2 min-h-2 w-2 min-w-2 rounded-full',
      statusColors({ dotBorder: color, dotBg: color }),
      className,
    )}
  />
);

const sizeVariants = cva('', {
  variants: {
    size: {
      md: 'h-6',
      large: 'h-8',
      xl: 'h-11',
    },
  },
});

export const Status = ({ className, color, variant, Icon, text, showDot = true, size = 'md' }: StatusProps) => {
  const isLarge = size === 'large';
  const isJumbo = size === 'xl';

  if (variant === 'icon' && Icon) {
    return (
      <div
        className={clsx(
          'relative flex h-8 w-8 items-center justify-center rounded-full border border-border-container bg-surface-container-lighter text-text-secondary',
          className,
        )}
      >
        {typeof Icon === 'function' ? <Icon /> : Icon}
        {color && (
          <Dot
            className={clsx(isJumbo && 'h-3 w-3', isLarge && 'h-2 w-2', 'absolute right-0.25 bottom-0.25')}
            color={color}
          />
        )}
      </div>
    );
  }
  if (variant !== 'dot') {
    return (
      <span
        className={clsx(
          'inline-flex items-center justify-center rounded-full text-nowrap',
          sizeVariants({ size }),
          statusColors({
            outlined: variant === 'outlined' ? color : undefined,
            contained: variant === 'contained' ? color : undefined,
          }),
          className,
        )}
      >
        {!Icon && showDot && color && (
          <Dot className={clsx(isJumbo && 'h-3 w-3', isLarge && 'h-2 w-2', 'ml-2')} color={color} />
        )}
        {typeof Icon === 'function' ? (
          <Icon
            className={clsx(
              statusColors({ icon: color }),
              isJumbo ? 'ml-2 h-6! w-6' : 'ml-2',
              isLarge ? 'ml-1.5 h-4! w-4' : 'ml-2',
            )}
          />
        ) : (
          <span className={clsx('ml-1 flex items-center [&_svg]:size-4!', statusColors({ icon: color }))}>{Icon}</span>
        )}
        <span
          className={clsx(
            isJumbo ? 'my-2.5 mr-4 ml-2 text-s18-sb' : 'mx-2 my-1 text-s11-sb',
            isLarge ? 'my-1 mr-3 ml-1.5 text-s16-sb' : 'mx-2 my-1 text-s11-sb',
            statusColors({ text: color }),
            'uppercase',
          )}
        >
          {text}
        </span>
      </span>
    );
  }
  return (
    color && <Dot className={clsx(isJumbo && 'h-3 w-3', isLarge && 'h-2 w-2', 'border', className)} color={color} />
  );
};
